import React, { useState, useEffect } from "react";
import { Card, Row, Col, Statistic, Table, Spin, Empty, Select, Tag, Button, message } from "antd";
import {
  DollarOutlined,
  TeamOutlined,
  RiseOutlined,
  FallOutlined,
  ProjectOutlined,
  ClockCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 8px 0; }
  p { color: #666; margin: 0; font-size: 14px; }
`;

const InsightsWrapper = styled.div`
  min-height: 100vh;

  .stats-row {
    margin-bottom: 24px;
  }

  .stat-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    height: 100%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    .stat-icon {
      font-size: 24px;
      margin-bottom: 12px;
    }

    .ant-statistic-title {
      color: #666;
      font-size: 13px;
    }

    .ant-statistic-content-value {
      color: #014c75;
      font-weight: 600;
    }
  }

  .chart-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    .chart-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      .chart-title {
        font-size: 16px;
        font-weight: 600;
        color: #014c75;
      }
    }
  }

  .table-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    .table-title {
      font-size: 16px;
      font-weight: 600;
      color: #014c75;
      margin-bottom: 16px;
    }
  }
`;

const CHART_COLORS = ["#014c75", "#00d9a9", "#faad14", "#1890ff", "#722ed1", "#eb2f96", "#52c41a", "#13c2c2"];

const formatCurrency = (value) => {
  if (value >= 10000000) {
    return `₹${(value / 10000000).toFixed(2)}Cr`;
  } else if (value >= 100000) {
    return `₹${(value / 100000).toFixed(2)}L`;
  } else if (value >= 1000) {
    return `₹${(value / 1000).toFixed(1)}K`;
  }
  return `₹${value?.toLocaleString() || 0}`;
};

const FinancialInsights = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [metrics, setMetrics] = useState({
    totalSpend: 0,
    totalTalentsHired: 0,
    totalEngagements: 0,
    avgCostPerTalent: 0,
    costSaved: 0,
    activeProjects: 0,
  });
  const [spendByMonth, setSpendByMonth] = useState([]);
  const [roleDistribution, setRoleDistribution] = useState([]);
  const [topRolesHired, setTopRolesHired] = useState([]);
  const [engagementsByValue, setEngagementsByValue] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  useEffect(() => {
    fetchFinancialInsights().catch(() => {});
  }, [selectedYear]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await fetchFinancialInsights();
      message.success("Financial data synced successfully");
    } catch (error) {
      message.error("Failed to sync financial data");
    } finally {
      setSyncing(false);
    }
  };

  const fetchFinancialInsights = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.BASE_URL}/customer-financial-insights?year=${selectedYear}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        const data = response.data.data;
        setMetrics({
          totalSpend: data.totalSpend || 0,
          totalTalentsHired: data.totalTalentsHired || 0,
          totalEngagements: data.totalEngagements || 0,
          avgCostPerTalent: data.avgCostPerTalent || 0,
          costSaved: data.costSaved || 0,
          activeProjects: data.activeProjects || 0,
        });
        setSpendByMonth(data.spendByMonth || []);
        setRoleDistribution(data.roleDistribution || []);
        setTopRolesHired(data.topRolesHired || []);
        setEngagementsByValue(data.engagementsByValue || []);
      }
    } catch (error) {
      console.error("Error fetching financial insights:", error);
      setMetrics({
        totalSpend: 0,
        totalTalentsHired: 0,
        totalEngagements: 0,
        avgCostPerTalent: 0,
        costSaved: 0,
        activeProjects: 0,
      });
      setSpendByMonth([]);
      setRoleDistribution([]);
      setTopRolesHired([]);
      setEngagementsByValue([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const topRolesColumns = [
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Talents Hired",
      dataIndex: "count",
      key: "count",
      align: "center",
    },
    {
      title: "Total Spend",
      dataIndex: "totalSpend",
      key: "totalSpend",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      title: "Avg Cost",
      dataIndex: "avgCost",
      key: "avgCost",
      align: "right",
      render: (value) => formatCurrency(value),
    },
  ];

  const engagementColumns = [
    {
      title: "Engagement",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "active" ? "green" : status === "closed" ? "default" : "orange"}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Talents",
      dataIndex: "talentCount",
      key: "talentCount",
      align: "center",
    },
    {
      title: "Total Value",
      dataIndex: "totalValue",
      key: "totalValue",
      align: "right",
      render: (value) => formatCurrency(value),
    },
  ];

  if (loading) {
    return (
      <InsightsWrapper>
        <div style={{ textAlign: "center", padding: "100px 0" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: "#666" }}>Loading Financial Insights...</p>
        </div>
      </InsightsWrapper>
    );
  }

  return (
    <InsightsWrapper>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <PageHeader style={{ marginBottom: 0 }}>
          <h1>Financial Insights</h1>
          <p>Comprehensive view of your spending, hiring trends, and engagement analytics</p>
        </PageHeader>
        <Button
          icon={<SyncOutlined spin={syncing} />}
          onClick={handleSync}
          loading={syncing}
        >
          Sync Now
        </Button>
      </div>

      <Row gutter={[16, 16]} className="stats-row">
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card" bordered={false}>
            <DollarOutlined className="stat-icon" style={{ color: "#014c75" }} />
            <Statistic
              title="Total Spend"
              value={metrics.totalSpend}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card" bordered={false}>
            <TeamOutlined className="stat-icon" style={{ color: "#00d9a9" }} />
            <Statistic title="Talents Hired" value={metrics.totalTalentsHired} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card" bordered={false}>
            <ProjectOutlined className="stat-icon" style={{ color: "#1890ff" }} />
            <Statistic title="Total Engagements" value={metrics.totalEngagements} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card" bordered={false}>
            <ClockCircleOutlined className="stat-icon" style={{ color: "#faad14" }} />
            <Statistic title="Active Projects" value={metrics.activeProjects} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card" bordered={false}>
            <RiseOutlined className="stat-icon" style={{ color: "#722ed1" }} />
            <Statistic
              title="Avg Cost/Talent"
              value={metrics.avgCostPerTalent}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card" bordered={false}>
            <FallOutlined className="stat-icon" style={{ color: "#52c41a" }} />
            <Statistic
              title="Cost Saved"
              value={metrics.costSaved}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={14}>
          <div className="chart-card" style={{ height: "100%" }}>
            <div className="chart-header">
              <span className="chart-title">Monthly Spend Analysis</span>
              <Select
                value={selectedYear}
                onChange={setSelectedYear}
                style={{ width: 100 }}
                options={yearOptions.map((year) => ({ label: year, value: year }))}
              />
            </div>
            {spendByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={spendByMonth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => formatCurrency(value)} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="spend" name="Spend" fill="#014c75" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No spend data available" />
            )}
          </div>
        </Col>

        <Col xs={24} lg={10}>
          <div className="chart-card" style={{ height: "100%" }}>
            <div className="chart-header">
              <span className="chart-title">Spend by Role</span>
            </div>
            {roleDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={roleDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={120}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="role"
                  >
                    {roleDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    formatter={(value) => <span style={{ color: "#333" }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No role distribution data" />
            )}
          </div>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <div className="table-card">
            <div className="table-title">Top Roles Hired</div>
            <Table
              columns={topRolesColumns}
              dataSource={topRolesHired}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
              size="small"
              rowKey="role"
              locale={{ emptyText: <Empty description="No data available" /> }}
            />
          </div>
        </Col>

        <Col xs={24} lg={12}>
          <div className="table-card">
            <div className="table-title">Engagements by Value</div>
            <Table
              columns={engagementColumns}
              dataSource={engagementsByValue}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
              size="small"
              rowKey="id"
              locale={{ emptyText: <Empty description="No data available" /> }}
            />
          </div>
        </Col>
      </Row>
    </InsightsWrapper>
  );
};

export default FinancialInsights;
